import { useEffect } from 'react'
import { create } from 'zustand'

export interface PageBackTarget {
  label: string
  to: string
}

interface PageBackState {
  target: PageBackTarget | null
  setTarget: (target: PageBackTarget | null) => void
}

/**
 * Lets a detail page say where "Back" should go (for example the school's Classes tab
 * with the same year) instead of the generic parent in the URL trail.
 */
export const usePageBackStore = create<PageBackState>(set => ({
  target: null,
  setTarget: target => set({ target }),
}))

/** Register the top bar's back destination for the lifetime of the calling page. */
export function useRegisterPageBack(target: PageBackTarget | null) {
  const setTarget = usePageBackStore(s => s.setTarget)
  const label = target?.label
  const to = target?.to
  useEffect(() => {
    setTarget(label && to ? { label, to } : null)
    return () => setTarget(null)
  }, [label, to, setTarget])
}

/**
 * Router state a link passes so the next page's "Back" returns here, e.g. a student
 * opened from a class goes "Back to Grade 3 A Math" instead of a generic parent.
 */
export function backLinkState(target: PageBackTarget): { backTo: PageBackTarget } {
  return { backTo: target }
}

/** The `backTo` a link passed in router state, when it is a usable in-app target. */
export function readBackTarget(state: unknown): PageBackTarget | null {
  const backTo = (state as { backTo?: Partial<PageBackTarget> } | null)?.backTo
  if (!backTo || typeof backTo.label !== 'string' || typeof backTo.to !== 'string') return null
  // Same-app paths only; never "//host" or absolute URLs from history state.
  if (!backTo.to.startsWith('/') || backTo.to.startsWith('//') || !backTo.label.trim()) return null
  return { label: backTo.label, to: backTo.to }
}
